import type { MainBootHandle } from './main';
import type { ClientMessage } from '@shared/protocol';
import {
  ALL_COLOR_IDS,
  COLOR_ID_TO_HEX,
  DEFAULT_CHARACTER_CUSTOMIZATION,
  type CharacterCustomization,
  type ColorId,
} from '@shared/game/types';

export const PLAYER_NAME_STORAGE_KEY = 'smashparty_player_name';
export const ONBOARDED_STORAGE_KEY = 'smashparty_onboarded';
export const PREFERRED_CUSTOMIZATION_STORAGE_KEY = 'smashparty_preferred_customization';
export const PREFERRED_PORTAL_COLOR_HEX_STORAGE_KEY = 'smashparty_preferred_portal_color_hex';
export const WARMUP_TUTORIAL_STORAGE_KEY = 'smashparty_warmup_tutorial_seen';
export const GAMEPLAY_TUTORIAL_STORAGE_KEY = 'smashparty_gameplay_onboarding_seen';
export const GAMEPLAY_TUTORIAL_LESSONS_STORAGE_KEY = 'smashparty_gameplay_lessons_seen';
export const HIDE_TUTORIALS_STORAGE_KEY = 'smashparty_settings.tutorials.hidden';

export interface PortalEntry {
  readonly ref: string | null;
  readonly username: string | null;
  readonly colorHex: string | null;
  readonly customization: CharacterCustomization | null;
}

export interface PortalMainMenuPayload {
  readonly ref: string | null;
  readonly username: string | null;
  readonly colorHex: string | null;
}

export type WarmupSubscribeMessage = Extract<ClientMessage, { type: 'subscribe_warmup_arena' }>;

const FALLBACK_USERNAME = 'Player';

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function sanitizeUsername(value: string | null): string | null {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0) return null;
  return trimmed.slice(0, 16);
}

export function normalizePortalColorHex(value: string | null): string | null {
  const cleaned = value?.trim().replace(/^#/, '').toLowerCase() ?? '';
  if (/^[0-9a-f]{3}$/u.test(cleaned)) {
    return cleaned
      .split('')
      .map((part) => part + part)
      .join('');
  }
  if (/^[0-9a-f]{6}$/u.test(cleaned)) return cleaned;
  return null;
}

function colorHexToRgb(hex: string): { r: number; g: number; b: number } {
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
}

function colorIdToHex(color: ColorId): string {
  return COLOR_ID_TO_HEX[color].toString(16).padStart(6, '0');
}

export function colorHexForCustomization(customization: CharacterCustomization | null): string {
  return colorIdToHex(customization?.color ?? 'red');
}

export function colorIdFromPortalHex(hex: string): ColorId {
  const normalized = normalizePortalColorHex(hex) ?? colorIdToHex('red');
  for (const colorId of ALL_COLOR_IDS) {
    if (colorIdToHex(colorId) === normalized) return colorId;
  }

  const target = colorHexToRgb(normalized);
  if (target.r > 200 && target.g < 80 && target.b > 200) return 'pink';

  let best: ColorId = 'red';
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const colorId of ALL_COLOR_IDS) {
    const candidate = colorHexToRgb(colorIdToHex(colorId));
    const distance =
      (target.r - candidate.r) ** 2 + (target.g - candidate.g) ** 2 + (target.b - candidate.b) ** 2;
    if (distance < bestDistance) {
      best = colorId;
      bestDistance = distance;
    }
  }
  return best;
}

function customizationFromHex(hex: string): CharacterCustomization {
  return {
    ...DEFAULT_CHARACTER_CUSTOMIZATION,
    color: colorIdFromPortalHex(hex),
  };
}

function isCustomization(value: unknown): value is CharacterCustomization {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<CharacterCustomization>;
  return (
    typeof candidate.color === 'string' &&
    ALL_COLOR_IDS.includes(candidate.color) &&
    typeof candidate.head === 'string' &&
    typeof candidate.eyes === 'string' &&
    typeof candidate.cape === 'string'
  );
}

export function savePreferredCustomization(customization: CharacterCustomization): void {
  const store = storage();
  if (store == null) return;
  try {
    store.setItem(PREFERRED_CUSTOMIZATION_STORAGE_KEY, JSON.stringify(customization));
    store.setItem(PREFERRED_PORTAL_COLOR_HEX_STORAGE_KEY, colorHexForCustomization(customization));
  } catch {
    // Best-effort only: private browsing can reject localStorage writes.
  }
}

export function savePreferredPortalColorHex(colorHex: string): void {
  const normalized = normalizePortalColorHex(colorHex);
  const store = storage();
  if (store == null || normalized == null) return;
  try {
    store.setItem(PREFERRED_PORTAL_COLOR_HEX_STORAGE_KEY, normalized);
  } catch {
    // Best-effort only.
  }
}

export function readPreferredCustomization(): CharacterCustomization | null {
  const store = storage();
  if (store == null) return null;
  try {
    const raw = store.getItem(PREFERRED_CUSTOMIZATION_STORAGE_KEY);
    if (raw == null) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isCustomization(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function readPreferredPortalColorHex(): string {
  const store = storage();
  if (store != null) {
    try {
      const raw = normalizePortalColorHex(store.getItem(PREFERRED_PORTAL_COLOR_HEX_STORAGE_KEY));
      if (raw != null) return raw;
    } catch {
      // Fall through to preferred customization.
    }
  }
  return colorHexForCustomization(readPreferredCustomization());
}

export function readPreferredPlayerName(): string {
  const store = storage();
  if (store == null) return FALLBACK_USERNAME;
  try {
    return sanitizeUsername(store.getItem(PLAYER_NAME_STORAGE_KEY)) ?? FALLBACK_USERNAME;
  } catch {
    return FALLBACK_USERNAME;
  }
}

export function readStoredPreferredPlayerName(): string | null {
  const store = storage();
  if (store == null) return null;
  try {
    return sanitizeUsername(store.getItem(PLAYER_NAME_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function savePreferredPlayerName(playerName: string): string | null {
  const sanitized = sanitizeUsername(playerName);
  const store = storage();
  if (store == null || sanitized == null) return sanitized;
  try {
    store.setItem(PLAYER_NAME_STORAGE_KEY, sanitized);
  } catch {
    // Best-effort only: private browsing can reject localStorage writes.
  }
  return sanitized;
}

export function hasSeenWarmupTutorial(): boolean {
  if (areTutorialsHidden()) return true;
  const store = storage();
  if (store == null) return true;
  try {
    return store.getItem(WARMUP_TUTORIAL_STORAGE_KEY) === 'true';
  } catch {
    return true;
  }
}

export function markWarmupTutorialSeen(): void {
  const store = storage();
  if (store == null) return;
  try {
    store.setItem(WARMUP_TUTORIAL_STORAGE_KEY, 'true');
  } catch {
    // Best-effort only: private browsing can reject localStorage writes.
  }
}

export function hasSeenGameplayTutorial(): boolean {
  if (areTutorialsHidden()) return true;
  const store = storage();
  if (store == null) return true;
  try {
    return store.getItem(GAMEPLAY_TUTORIAL_STORAGE_KEY) === 'true';
  } catch {
    return true;
  }
}

export function markGameplayTutorialSeen(): void {
  const store = storage();
  if (store == null) return;
  try {
    store.setItem(GAMEPLAY_TUTORIAL_STORAGE_KEY, 'true');
  } catch {
    // Best-effort only: private browsing can reject localStorage writes.
  }
}

function readGameplayTutorialLessonIds(store: Storage): string[] {
  const raw = store.getItem(GAMEPLAY_TUTORIAL_LESSONS_STORAGE_KEY);
  if (raw == null) return [];
  const parsed = JSON.parse(raw) as unknown;
  return Array.isArray(parsed) ? parsed.filter((value) => typeof value === 'string') : [];
}

export function hasSeenGameplayTutorialLesson(lessonId: string): boolean {
  if (areTutorialsHidden()) return true;
  const store = storage();
  if (store == null) return true;
  try {
    return readGameplayTutorialLessonIds(store).includes(lessonId);
  } catch {
    return true;
  }
}

export function markGameplayTutorialLessonSeen(lessonId: string): void {
  const store = storage();
  if (store == null) return;
  try {
    const ids = new Set(readGameplayTutorialLessonIds(store));
    ids.add(lessonId);
    store.setItem(GAMEPLAY_TUTORIAL_LESSONS_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // Best-effort only: private browsing can reject localStorage writes.
  }
}

export function areTutorialsHidden(): boolean {
  const store = storage();
  if (store == null) return false;
  try {
    return store.getItem(HIDE_TUTORIALS_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setTutorialsHidden(hidden: boolean): void {
  const store = storage();
  if (store == null) return;
  try {
    store.setItem(HIDE_TUTORIALS_STORAGE_KEY, String(hidden));
  } catch {
    // Best-effort only.
  }
}

export function buildWarmupSubscribeMessage(opts?: {
  readonly preferredColor?: string | null;
  readonly playerName?: string | null;
  readonly customization?: CharacterCustomization | null;
  readonly isReady?: boolean;
}): WarmupSubscribeMessage {
  const playerName = sanitizeUsername(opts?.playerName ?? null) ?? readStoredPreferredPlayerName();
  const customization =
    opts?.customization === undefined ? readPreferredCustomization() : opts.customization;
  const preferredColor = normalizePortalColorHex(opts?.preferredColor ?? null);
  const message: WarmupSubscribeMessage = {
    type: 'subscribe_warmup_arena',
    ...(playerName == null ? {} : { playerName }),
    ...(customization == null
      ? preferredColor == null
        ? {}
        : { preferredColor }
      : { customization }),
    ...(typeof opts?.isReady === 'boolean' ? { isReady: opts.isReady } : {}),
  };
  return message;
}

export function detectPortalEntry(search = window.location.search): PortalEntry | null {
  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  if (params.get('portal') !== 'true') return null;

  const username = sanitizeUsername(params.get('username'));
  const colorHex = normalizePortalColorHex(params.get('color'));
  return {
    ref: sanitizeRef(params.get('ref')),
    username,
    colorHex,
    customization: colorHex == null ? null : customizationFromHex(colorHex),
  };
}

function sanitizeRef(ref: string | null): string | null {
  const trimmed = ref?.trim() ?? '';
  if (trimmed.length === 0) return null;
  if (/[\s<>"]/u.test(trimmed)) return null;
  return trimmed.slice(0, 256);
}

function setStorageValue(key: string, value: string): void {
  const store = storage();
  if (store == null) return;
  try {
    store.setItem(key, value);
  } catch {
    // Best-effort only.
  }
}

export function applyPortalEntry(handle: MainBootHandle, entry: PortalEntry): void {
  if (entry.username != null) {
    setStorageValue(PLAYER_NAME_STORAGE_KEY, entry.username);
  }
  setStorageValue(ONBOARDED_STORAGE_KEY, 'true');
  if (entry.customization != null) {
    savePreferredCustomization(entry.customization);
  }
  if (entry.colorHex != null) {
    savePreferredPortalColorHex(entry.colorHex);
  }
  handle.wsClient.send(
    buildWarmupSubscribeMessage({
      playerName: entry.username,
      customization: null,
      preferredColor: entry.colorHex ?? colorHexForCustomization(readPreferredCustomization()),
      isReady: hasSeenWarmupTutorial(),
    }),
  );
  handle.screenRouter.navigate('warmup-arena-overlay', {
    portal: {
      ref: entry.ref,
      username: entry.username,
      colorHex: entry.colorHex,
    } satisfies PortalMainMenuPayload,
  });
}

export function applyNativeWarmupEntry(handle: MainBootHandle): void {
  const storedName = readStoredPreferredPlayerName();
  if (storedName == null) {
    showNativeWarmupNameModal((playerName) => {
      enterNativeWarmup(handle, playerName);
    });
    return;
  }
  enterNativeWarmup(handle, storedName);
}

function enterNativeWarmup(handle: MainBootHandle, playerName: string): void {
  setStorageValue(ONBOARDED_STORAGE_KEY, 'true');
  handle.wsClient.send(
    buildWarmupSubscribeMessage({
      playerName,
      customization: null,
      preferredColor: colorHexForCustomization(readPreferredCustomization()),
      isReady: hasSeenWarmupTutorial(),
    }),
  );
  handle.screenRouter.navigate('warmup-arena-overlay', { portal: null });
}

function showNativeWarmupNameModal(onSubmit: (playerName: string) => void): void {
  const existing = document.querySelector('[data-warmup-name-gate="true"]');
  existing?.remove();

  const overlay = document.createElement('div');
  overlay.dataset.warmupNameGate = 'true';
  overlay.style.cssText = [
    'position: fixed',
    'inset: 0',
    'z-index: 1000',
    'display: grid',
    'place-items: center',
    'background: rgba(5, 8, 14, 0.64)',
    'backdrop-filter: blur(6px)',
  ].join('; ');

  const panel = document.createElement('form');
  panel.style.cssText = [
    'width: min(420px, calc(100vw - 32px))',
    'display: flex',
    'flex-direction: column',
    'gap: 14px',
    'padding: 22px',
    'box-sizing: border-box',
    'border-radius: 8px',
    'background: rgba(20,24,34,0.96)',
    'border: 1px solid rgba(255,255,255,0.18)',
    'box-shadow: 0 18px 42px rgba(0,0,0,0.45)',
  ].join('; ');
  overlay.appendChild(panel);

  const title = document.createElement('h2');
  title.textContent = 'SET NICK';
  title.style.cssText = [
    "font-family: var(--typography-family-headline, 'Fredoka One', 'Arial Black', sans-serif)",
    'font-size: 26px',
    'line-height: 1',
    'font-weight: 800',
    'margin: 0',
    'color: #FFFFFF',
    'text-shadow: 0 2px 0 #000000',
    'text-align: center',
  ].join('; ');
  panel.appendChild(title);

  const input = document.createElement('input');
  input.type = 'text';
  input.maxLength = 16;
  input.autocomplete = 'name';
  input.spellcheck = false;
  input.placeholder = 'Your nick';
  input.style.cssText = [
    'height: 46px',
    'box-sizing: border-box',
    'border-radius: 8px',
    'border: 2px solid rgba(255,255,255,0.22)',
    'background: rgba(0,0,0,0.32)',
    'color: #FFFFFF',
    "font-family: var(--typography-family-headline, 'Fredoka One', 'Arial Black', sans-serif)",
    'font-size: 20px',
    'font-weight: 800',
    'letter-spacing: 0',
    'padding: 0 12px',
    'outline: none',
    'text-align: center',
  ].join('; ');
  panel.appendChild(input);

  const error = document.createElement('div');
  error.style.cssText = [
    'min-height: 16px',
    "font-family: var(--typography-family-body, 'Nunito Sans', sans-serif)",
    'font-size: 13px',
    'font-weight: 900',
    'color: #FFB0A0',
    'text-align: center',
  ].join('; ');
  panel.appendChild(error);

  const submit = document.createElement('button');
  submit.type = 'submit';
  submit.textContent = 'ENTER WARMUP';
  submit.style.cssText = [
    'height: 44px',
    'border-radius: 8px',
    'border: 1px solid #8A5A10',
    'background: linear-gradient(to bottom, #F0B838, #C4841C)',
    'color: #FFFFFF',
    'text-shadow: 0 1px 0 #000000, 0 2px 6px rgba(0,0,0,0.45)',
    "font-family: var(--typography-family-headline, 'Fredoka One', 'Arial Black', sans-serif)",
    'font-size: 15px',
    'font-weight: 800',
    'letter-spacing: 0',
    'cursor: pointer',
  ].join('; ');
  panel.appendChild(submit);

  panel.addEventListener('submit', (event) => {
    event.preventDefault();
    const playerName = savePreferredPlayerName(input.value);
    if (playerName == null) {
      error.textContent = 'Enter 1-16 characters.';
      input.focus();
      return;
    }
    overlay.remove();
    onSubmit(playerName);
  });

  document.body.appendChild(overlay);
  input.focus();
}

export function buildVibejamPortalUrl(opts?: {
  readonly username?: string;
  readonly colorHex?: string;
  readonly refHost?: string;
}): string {
  const url = new URL('https://vibejam.cc/portal/2026');
  url.searchParams.set('username', opts?.username ?? readPreferredPlayerName());
  url.searchParams.set(
    'color',
    normalizePortalColorHex(opts?.colorHex ?? null) ?? readPreferredPortalColorHex(),
  );
  url.searchParams.set('speed', '5');
  url.searchParams.set('ref', opts?.refHost ?? window.location.host);
  return url.toString();
}

export function buildPortalReturnUrl(entry: PortalMainMenuPayload): string {
  const refUrl = parsePortalRefUrl(entry.ref);
  if (refUrl == null) return buildVibejamPortalUrl();
  refUrl.searchParams.set('portal', 'true');
  refUrl.searchParams.set('ref', window.location.host);
  refUrl.searchParams.set('username', entry.username ?? readPreferredPlayerName());
  refUrl.searchParams.set('color', entry.colorHex ?? readPreferredPortalColorHex());
  return refUrl.toString();
}

function parsePortalRefUrl(ref: string | null): URL | null {
  if (ref == null) return null;
  try {
    const url = /^https?:\/\//iu.test(ref) ? new URL(ref) : new URL(`https://${ref}`);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url;
  } catch {
    return null;
  }
}
